import {ProfileSocialProvider} from '../constants';

interface SocialProfileIdentity {
  usernameOrHandle?: string;
  displayName?: string;
}

const HANDLE_PROVIDERS = new Set<ProfileSocialProvider>([
  'instagram',
  'twitter',
  'tiktok',
]);

const GENERIC_LABELS: Record<ProfileSocialProvider, string> = {
  facebook: 'Facebook Page',
  instagram: 'Instagram profile',
  twitter: 'X profile',
  linkedin: 'LinkedIn profile',
  youtube: 'YouTube channel',
  tiktok: 'TikTok profile',
  website: 'Website',
};

const cleanLabel = (value?: string) => value?.trim().replace(/\s+/g, ' ') || '';

const formatHandle = (value: string) => `@${value.replace(/^@+/, '')}`;

const safeUrl = (href?: string): URL | null => {
  if (!href) {
    return null;
  }

  try {
    return new URL(/^https?:\/\//i.test(href) ? href : `https://${href}`);
  } catch (error) {
    return null;
  }
};

const readPathSegments = (url: URL) => url.pathname
  .split('/')
  .filter(Boolean)
  .map((segment) => {
    try {
      return decodeURIComponent(segment);
    } catch (error) {
      return segment;
    }
  });

const readProfileIdentifier = (
  provider: ProfileSocialProvider,
  href?: string,
): string => {
  const url = safeUrl(href);

  if (!url) {
    return '';
  }

  const segments = readPathSegments(url);

  switch (provider) {
    case 'facebook': {
      const firstSegment = segments[0];

      if (firstSegment === 'profile.php') {
        return '';
      }

      if (firstSegment?.toLowerCase() === 'pages') {
        const pageSlug = cleanLabel(segments[1]);
        return /^\d+$/.test(pageSlug) ? '' : pageSlug;
      }

      return firstSegment
        && !/^\d+$/.test(firstSegment)
        && !['groups', 'share'].includes(firstSegment.toLowerCase())
          ? cleanLabel(firstSegment)
          : '';
    }
    case 'instagram':
      return segments[0] && !['accounts', 'p', 'reel', 'stories'].includes(segments[0].toLowerCase())
        ? cleanLabel(segments[0])
        : '';
    case 'twitter':
      return segments[0] && !['home', 'i', 'intent', 'share'].includes(segments[0].toLowerCase())
        ? cleanLabel(segments[0])
        : '';
    case 'linkedin': {
      const profileTypeIndex = segments.findIndex((segment) => (
        segment.toLowerCase() === 'in' || segment.toLowerCase() === 'company'
      ));

      return profileTypeIndex >= 0 ? cleanLabel(segments[profileTypeIndex + 1]) : '';
    }
    case 'youtube': {
      const handle = segments.find((segment) => segment.startsWith('@'));

      if (handle) {
        return cleanLabel(handle);
      }

      const profileTypeIndex = segments.findIndex((segment) => (
        ['c', 'channel', 'user'].includes(segment.toLowerCase())
      ));

      return profileTypeIndex >= 0 && segments[profileTypeIndex].toLowerCase() !== 'channel'
        ? cleanLabel(segments[profileTypeIndex + 1])
        : '';
    }
    case 'tiktok':
      return cleanLabel(segments.find((segment) => segment.startsWith('@')));
    case 'website':
      return url.hostname.replace(/^www\./i, '');
    default:
      return '';
  }
};

/** Returns compact identity copy for a social card without exposing its full URL. */
export const getSocialProfileLabel = (
  provider: ProfileSocialProvider,
  connection?: SocialProfileIdentity,
  href?: string,
): string => {
  const username = cleanLabel(connection?.usernameOrHandle);
  const displayName = cleanLabel(connection?.displayName);
  const urlIdentifier = readProfileIdentifier(provider, href);

  if (provider === 'website') {
    return urlIdentifier || GENERIC_LABELS.website;
  }

  if (HANDLE_PROVIDERS.has(provider)) {
    const handle = username || urlIdentifier;
    return handle ? formatHandle(handle) : displayName || GENERIC_LABELS[provider];
  }

  if (provider === 'youtube') {
    if (username) {
      return username.startsWith('@') ? username : formatHandle(username);
    }

    return displayName || urlIdentifier || GENERIC_LABELS.youtube;
  }

  return username || displayName || urlIdentifier || GENERIC_LABELS[provider];
};
